import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button } from '@cloudscape-design/components';
import { RbacContext, useAuth, type UserAccess, type ImpersonationInfo } from '@deloitte-us-consulting/gp-sdk';

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

/**
 * Shell-owned RBAC context. Fetches GET /api/rbac/me and provides it via the
 * SDK's RbacContext; every consumer (useRoles/RoleGate) reads from here. See
 * docs/adr/0003-rbac-approach.md and the gp-platform skill references/rbac.md.
 *
 * Uses the shell's own QueryClient (provisioned in main.tsx) — this is its
 * first real consumer. `enabled: !!accessToken` means this never fires in
 * STANDALONE dev (DEV_USER's accessToken is always null there); useRoles()
 * falls back to the auth user's raw Cognito groups in that case, so the dev
 * admin experience is unaffected.
 *
 * Also renders the persistent, unmissable impersonation banner (ADR 0010) —
 * done here, not as a separate component someone could forget to mount, since
 * this provider already wraps the entire app and already holds the /me data
 * that carries `impersonation`.
 */
export function RbacProvider({ children }: { children: ReactNode }) {
  const { accessToken } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['rbac', 'me', accessToken],
    queryFn: async (): Promise<UserAccess> => {
      const res = await fetch(`${API_URL}/api/rbac/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error(`GET /api/rbac/me failed: ${res.status}`);
      return res.json() as Promise<UserAccess>;
    },
    enabled: !!accessToken,
    staleTime: 30_000,
  });

  const value = { access: data ?? null, isLoading };

  return (
    <RbacContext.Provider value={value}>
      {data?.impersonation && <ImpersonationBanner info={data.impersonation} accessToken={accessToken} />}
      {children}
    </RbacContext.Provider>
  );
}

function impersonationLabel(info: ImpersonationInfo): string {
  if (info.mode === 'user') return `Viewing as user ${info.targetUserId}`;
  const parts: string[] = [];
  if (info.allBypass) parts.push('All alliances');
  else if (info.allianceIds?.length) parts.push(`alliances: ${info.allianceIds.join(', ')}`);
  if (info.roleIds?.length) parts.push(`roles: ${info.roleIds.join(', ')}`);
  return parts.length ? `Viewing as: ${parts.join('; ')}` : 'Viewing a synthetic (no-access) preview';
}

function ImpersonationBanner({ info, accessToken }: { info: ImpersonationInfo; accessToken: string | null }) {
  const queryClient = useQueryClient();
  const [stopping, setStopping] = useState(false);

  const stop = async () => {
    setStopping(true);
    try {
      await fetch(`${API_URL}/api/impersonation/stop`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } finally {
      setStopping(false);
      // Refetch /me so the banner disappears and every RBAC check reverts
      // to the true caller's own real access.
      void queryClient.invalidateQueries({ queryKey: ['rbac', 'me'] });
    }
  };

  return (
    <Alert
      type="warning"
      header="Impersonation active — read-only"
      action={<Button onClick={stop} loading={stopping}>Exit impersonation</Button>}
    >
      {impersonationLabel(info)}. Expires {new Date(info.expiresAt).toLocaleTimeString()}. No action (approving a
      request, registering an app, etc.) can be taken while this is active.
    </Alert>
  );
}
